import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation } from "react-router-dom";

import {
  PageBodyTitle,
  Card,
  Table,
  ListPageFilterBar,
  StatusBadge,
} from "../../../design-system";
import { theme } from "../../../design-system/theme";

import {
  getInventoryList,
  allotVpaByAdmin,
  getMerchantListingMob,
} from "../../../../utils/api";

import { CsvDown } from "../../../integrations/export";
import { CsvDownApi } from "../../../integrations/export";
import { getAssignedUserDetailsRequest } from "../../../../utils/api";
import config from "../../../../utils/config";
//
// 🔹 STATUS OPTIONS
//
const STATUS_OPTIONS = [
  { value: "AVAILABLE", label: "AVAILABLE" },
  { value: "ASSIGNED", label: "ASSIGNED" },
  { value: "INACTIVE", label: "INACTIVE" }, 
];
 const CSV_HEADERS = [
  { label: "ID", key: "id" },
  { label: "VPA", key: "vpa" },
  { label: "User ID", key: "userId" },
  { label: "Handle", key: "handle" },
  { label: "Order ID", key: "orderId" },
  { label: "Multiple VPA", key: "isMultipleVpa" },
  { label: "Status", key: "status" },
  { label: "Created", key: "createdAt" },
  { label: "Updated", key: "updatedAt" },
  { label: "Allocated", key: "allocatedAt" },
];
//
// 🔹 LAYOUT
//
const PageWrapper = styled.div`
  max-width: 1440px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.lg};
  width: 100%;
  align-items: center;
`;

//
// 🔹 HELPERS
//
const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN");
};

//
// 🔥 MAIN COMPONENT
//
export default function QRInventoryV2() {
  const location = useLocation();

  // ✅ Read orderId from URL
  const queryParams = new URLSearchParams(location.search);
  const orderIdFromURL = queryParams.get("orderId");

  //
  // 🔹 FILTER INITIAL (dynamic)
  //
  const getInitialFilters = () => [
    {
      name: "orderId",
      type: "text",
      value: orderIdFromURL || "",
      placeholder: "Order ID",
      disabled: !!orderIdFromURL, // optional UX improvement
    },
    { name: "mobileNumber", type: "text", value: "", placeholder: "Mobile Number" },
    {
      name: "status",
      type: "select",
      value: "",
      placeholder: "Status",
      options: STATUS_OPTIONS,
    },
    { name: "fromDate", type: "date", value: "" },
    { name: "toDate", type: "date", value: "" },
  ];

  const [controls, setControls] = useState(getInitialFilters());
  const [appliedControls, setAppliedControls] = useState(getInitialFilters());

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  const [viewItem, setViewItem] = useState(null);
  const [allocateItem, setAllocateItem] = useState(null);

  const [mobile, setMobile] = useState("");
  const [merchant, setMerchant] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [assignedUser, setAssignedUser] = useState(null);
  const [assignedLoading, setAssignedLoading] = useState(false); 
  const [csvData, setCsvData] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const appliedRef = useRef(appliedControls);
  appliedRef.current = appliedControls;

  //
  // 🔹 BUILD PAYLOAD
  //
  const buildPayload = (controls, pageIndex, pageSize) => {
    const get = (n) => controls.find((c) => c.name === n)?.value ?? "";

    return {
      pagination: { pageNo: pageIndex + 1, pageSize },
      orderId: get("orderId") || "",
      mobileNumber: get("mobileNumber") || "",
      status: get("status") || "",
      fromDate: get("fromDate") || "",
      toDate: get("toDate") || "",
    };
  };

  //
  // 🔹 FETCH
  //
  const fetchList = useCallback(async (payload) => {
  setLoading(true);
  try {
    const res = await getInventoryList(payload);
    const response = res?.data ?? res;
   
    if (response?.code === "0000") {
      const meta = response.metaInfo;
     console.log("API ITEM 👉", meta.data?.[4]);
      const content = (meta.data || []).map((item) => ({
        ...item.vpa,
        orderId: item.vpa?.orderId || "-",
      }));

      setRows(content);
      setTotalElements(Number(meta.totalElements) || 0);

    } 
    else if (response?.code === "0047") {
      // ✅ HANDLE NO DATA CASE
      setRows([]);
      setTotalElements(0);
      // optional:
      // toast.info(response.message);
    } 
    else {
      toast.error(response?.message || "Failed to fetch inventory");
      setRows([]);           // 🔥 IMPORTANT
      setTotalElements(0);
    }

  } catch (err) {
    toast.error(err?.message || "Network error");
    setRows([]);             // 🔥 IMPORTANT
    setTotalElements(0);
  } finally {
    setLoading(false);
  }
}, []);
  
  //
  // 🔹 INITIAL LOAD / REFETCH
  //
  useEffect(() => {
    fetchList(buildPayload(appliedControls, pageIndex, pageSize));
  }, [appliedControls, pageIndex, pageSize, fetchList]);
  useEffect(() => {
  if (viewItem) {
    setAssignedLoading(true);

    const payload = {
      vpa: viewItem?.vpa,
      userId: viewItem?.userId || "",
      pagination: { pageNo: 1, pageSize: 10 },
    };

    getAssignedUserDetailsRequest(payload)
      .then((res) => {
        if (res?.data?.code === "0000") {
          setAssignedUser(res?.data?.metaInfo?.data?.[0]?.userProfile);
        } else {
          setAssignedUser(null);
        }
      })
      .catch(() => setAssignedUser(null))
      .finally(() => setAssignedLoading(false));
  }
}, [viewItem]);
  //
  // 🔹 FILTERS
  //
  const handleFilterChange = (name, value) => {
    setControls((prev) =>
      prev.map((c) => (c.name === name ? { ...c, value } : c))
    );
  };

  const onSearch = () => {
    setAppliedControls(controls.map((c) => ({ ...c })));
    setPageIndex(0);
  };

  const onReset = () => {
    const reset = getInitialFilters();
    setControls(reset);
    setAppliedControls(reset);
    setPageIndex(0);
  };

  //
  // 🔹 ACTIONS
  //
  const handleAllocate = async () => {
    try {
      await allotVpaByAdmin(allocateItem.vpa, merchant.id);
      toast.success("Allocated successfully");
      setAllocateItem(null);
      fetchList(buildPayload(appliedRef.current, pageIndex, pageSize));
    } catch {
      toast.error("Allocation failed");
    }
  };

  const handleMerchantSearch = async () => {
  setLookupError("");
  setMerchant(null);

  if (!/^[0-9]{10}$/.test(mobile)) {
    setLookupError("Please enter a valid 10-digit mobile number");
    return;
  }

  setLookupLoading(true);

  try {
    const res = await getMerchantListingMob({
      mobileNumber: mobile,
      userType: "ROLE_BUSINESS_MERCHANT_USER",
      pagination: { pageNo: 1, pageSize: 10 },
    });

    const m = res?.data?.data?.content?.[0];

    if (!m) {
      setLookupError("No merchant found");
    } else {
      setMerchant(m);
      setLookupError(""); // clear error
    }
  } catch (err) {
    setLookupError(err?.message || "Lookup failed");
  } finally {
    setLookupLoading(false);
  }
};
  useEffect(() => {
    if (allocateItem) {
      setMobile("");
      setMerchant(null);
    }
  }, [allocateItem]);
useEffect(() => {
  fetchAllDataForCSV();
}, [appliedControls]); // same filters
const fetchAllDataForCSV = async () => {
  try {
    setCsvLoading(true);

    let page = 0;
    let allData = [];
    let totalPages = 1;

    do {
      const payload = buildPayload(appliedRef.current, page, 500);

      const res = await getInventoryList(payload);
      const response = res?.data ?? res;

      if (response?.code !== "0000") break;

      const meta = response.metaInfo;

      const content = (meta.data || []).map((item) => ({
        ...item.vpa,
        orderId: item.vpa?.orderId || "-",
      }));

      allData = [...allData, ...content];

      totalPages = meta.totalPages || 1;
      page++;

    } while (page < totalPages);

    setCsvData(allData);

  } catch (err) {
    toast.error("CSV fetch failed");
  } finally {
    setCsvLoading(false);
  }
};
  //
  // 🔹 TABLE
  //
  const columns = [
    { key: "id", label: "ID" },
    { key: "vpa", label: "VPA" },
    { key: "userId", label: "User ID", render: (_v, r) => r.userId || "-" },
    { key: "handle", label: "Handle" },
    { key: "orderId", label: "Order ID", render: (_v, r) => r.orderId || "-" },
    {
      key: "isMultipleVpa",
      label: "Multiple VPA",
      render: (_v, r) => (r.isMultipleVpa ? "YES" : "NO"),
    },
    {
      key: "status",
      label: "Status",
      render: (_v, r) => (r?.status ? <StatusBadge status={r.status} /> : "-"),
    },
    {
      key: "createdAt",
      label: "Created",
      render: (_v, r) => formatDateTime(r.createdAt),
    },
    {
      key: "updatedAt",
      label: "Updated",
      render: (_v, r) => formatDateTime(r.updatedAt),
    },
    {
      key: "allocatedAt",
      label: "Allocated",
      render: (_v, r) => formatDateTime(r.allocatedAt),
    },
    {
      key: "actions",
      label: "Actions",
      render: (_v, row) => (
        <RowMenu row={row} onView={setViewItem} onAllocate={setAllocateItem} />
      ),
    },
  ];

  return (
    <PageWrapper>
      <TopBar>
        <PageBodyTitle>VPA Inventory Management</PageBodyTitle>

        <div style={{ marginLeft: "auto" }}>
        <CsvDown
         headers={CSV_HEADERS}
         exportRows={csvData}   // ✅ full dataset
         title="Download CSV"
         reportName="VPA_Inventory.csv"
       />
        </div>
      </TopBar>

      <Card>
        <ListPageFilterBar
          filters={controls}
          onFilterChange={handleFilterChange}
          onSearch={onSearch}
          onReset={onReset}
        />

        <Table
          data={rows}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            currentPage: pageIndex + 1,
            pageSize,
            total: totalElements,
            onPageChange: (p) => setPageIndex(p - 1),
            onPageSizeChange: (s) => {
              setPageSize(s);
              setPageIndex(0);
            },
          }}
        />
      </Card>

      {/* VIEW MODAL */}
      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth>
  <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
    QR Details — {viewItem?.vpa}
    <IconButton onClick={() => setViewItem(null)} size="small">
      <CloseIcon />
    </IconButton>
  </DialogTitle>

  <DialogContent dividers>
    {viewItem && (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography><b>ID:</b> {viewItem.id}</Typography>
        <Typography><b>VPA:</b> {viewItem.vpa}</Typography>
        <Typography><b>User ID:</b> {viewItem.userId || "-"}</Typography>
        <Typography><b>Handle:</b> {viewItem.handle}</Typography>
        <Typography><b>Order ID:</b> {viewItem.orderId || "-"}</Typography>
        <Typography><b>Status:</b> {viewItem.status}</Typography>
        <Typography><b>Created:</b> {formatDateTime(viewItem.createdAt)}</Typography>
        <Typography><b>Updated:</b> {formatDateTime(viewItem.updatedAt)}</Typography>
        <Typography><b>Allocated:</b> {formatDateTime(viewItem.allocatedAt)}</Typography>

        {/* 🔹 Assigned User Section */}
        <Typography sx={{ mt: 2, fontWeight: 600 }}>
          Assigned User:
        </Typography>

        {assignedLoading ? (
          <Typography>Loading...</Typography>
        ) : assignedUser ? (
          <Box sx={{ ml: 1 }}>
            <Typography><b>Name:</b> {assignedUser.fullName}</Typography>
            <Typography><b>Mobile:</b> {assignedUser.mobile}</Typography>
            <Typography><b>Email:</b> {assignedUser.email || "-"}</Typography>
            <Typography><b>Status:</b> {assignedUser.status}</Typography>
          </Box>
        ) : (
          <Typography>No assigned user</Typography>
        )}
      </Box>
    )}
  </DialogContent>

  <DialogActions>
    <Button variant="outlined"  onClick={() => setViewItem(null)}>
      Close
    </Button>
  </DialogActions>
</Dialog>

      {/* ALLOCATE MODAL */}
<Dialog open={!!allocateItem} onClose={() => setAllocateItem(null)} maxWidth="sm" fullWidth>
  <DialogTitle
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontWeight: 600,
    }}
  >
    Allocate QR — {allocateItem?.vpa || allocateItem?.id}
    <IconButton onClick={() => setAllocateItem(null)} size="small">
      <CloseIcon />
    </IconButton>
  </DialogTitle>

  <DialogContent dividers>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Search by mobile number and click the Search button to lookup merchant.
      </Typography>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          label="Mobile number"
          placeholder="Enter 10-digit mobile number"
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          fullWidth
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root.Mui-focused": {
              "& fieldset": { borderColor: "black" },
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "black",
            },
          }}
        />

        <Button
          variant="contained"
          onClick={handleMerchantSearch}
          disabled={lookupLoading || !mobile}
          sx={{
            whiteSpace: "nowrap",
          }}
        >
          {lookupLoading ? "Searching..." : "Search"}
        </Button>
      </Box>

      <Box sx={{ minHeight: 28 }}>
        {lookupLoading && (
          <Typography variant="body2" color="text.secondary">
            Looking up merchant...
          </Typography>
        )}
        {!lookupLoading && lookupError && (
           <Typography variant="body2" color="error">
             {lookupError}
           </Typography>
         )}
        {!lookupLoading && merchant && (
          <Typography variant="body2" color="text.primary">
            Merchant: {merchant?.businessName || merchant?.name || "-"}
          </Typography>
        )}
      </Box>
    </Box>
  </DialogContent>

  <DialogActions sx={{ px: 3, pb: 2 }}>
    <Button
      variant="outlined"
      onClick={() => setAllocateItem(null)}
    >
      Cancel
    </Button>

    <Button
      variant="contained"
      onClick={handleAllocate}
      disabled={!merchant}
     
    >
      Allocate
    </Button>
  </DialogActions>
</Dialog>
    </PageWrapper>
  );
}
//
// 🔹 ROW MENU
//
const RowMenu = ({ row, onView, onAllocate }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>

      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem
          disabled={row.status !== "ASSIGNED"}
          onClick={() => {
            setAnchorEl(null);
            onView(row);
          }}
        >
          View
        </MenuItem>

        <MenuItem
          disabled={row.status !== "AVAILABLE"}
          onClick={() => {
            setAnchorEl(null);
            onAllocate(row);
          }}
        >
          Allocate
        </MenuItem>
      </Menu>
    </>
  );
};